import { Link, useNavigate } from 'react-router-dom';
import type { TariffDto } from '@lex/contracts';
import { Button } from '@lex/ui';
import { CheckCircleIcon } from '../icons';
import { Glow, SectionHeader } from './primitives';

function TariffCard({
  tariff,
  onSelect,
}: {
  tariff: TariffDto;
  onSelect?: (tariff: TariffDto) => void;
}) {
  const navigate = useNavigate();
  const highlighted = tariff.highlighted;

  return (
    <div
      className={`flex h-full flex-col gap-5 rounded-3xl p-7 md:p-9 ${
        highlighted
          ? 'bg-ink-gradient shadow-ink'
          : 'border border-line bg-white'
      }`}
    >
      <div className="flex items-center gap-3">
        <span
          className={`text-[24px] font-semibold tracking-[-0.005em] md:text-[26px] ${
            highlighted ? 'text-white' : 'text-ink'
          }`}
        >
          {tariff.name}
        </span>
        {tariff.badge ? (
          <span className="ml-auto rounded-lg bg-accent-gradient px-3 py-1.5 text-[12px] font-semibold text-white">
            {tariff.badge}
          </span>
        ) : null}
      </div>

      <p className={`text-[15px] leading-[1.4] md:text-base ${highlighted ? 'text-muted-sky' : 'text-muted'}`}>
        {tariff.description}
      </p>

      <div className="flex items-baseline gap-2">
        <span
          className={`text-[48px] font-bold leading-none tracking-[-0.03em] md:text-[60px] ${
            highlighted ? 'text-white' : 'text-ink'
          }`}
        >
          {tariff.price.toLocaleString('ru-RU')}
        </span>
        <span className={`text-[26px] font-bold ${highlighted ? 'text-white' : 'text-ink'}`}>
          {tariff.currency}
        </span>
        {tariff.period ? (
          <span className={`text-[15px] ${highlighted ? 'text-muted-sky' : 'text-muted'}`}>
            {tariff.period}
          </span>
        ) : null}
      </div>

      <div className={`h-px w-full ${highlighted ? 'bg-panel-border' : 'bg-line'}`} />

      <ul className="flex flex-col gap-3.5">
        {tariff.features.map((feature) => (
          <li key={feature} className="flex items-start gap-3">
            <CheckCircleIcon className="mt-0.5 h-[22px] w-[22px] shrink-0 text-accent" />
            <span
              className={`text-[15px] leading-[1.5] md:text-base ${highlighted ? 'text-white' : 'text-ink'}`}
            >
              {feature}
            </span>
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-3">
        <Button
          variant={highlighted ? 'accent' : 'outline'}
          size="lg"
          className="w-full rounded-xl"
          onClick={() => (onSelect ? onSelect(tariff) : navigate(tariff.ctaHref))}
        >
          {tariff.ctaLabel}
        </Button>
      </div>
    </div>
  );
}

export function TariffsSection({
  tariffs,
  showHeader = true,
  onSelect,
}: {
  tariffs: TariffDto[];
  showHeader?: boolean;
  onSelect?: (tariff: TariffDto) => void;
}) {
  return (
    <section id="tariffs" className="scroll-mt-24 relative overflow-hidden px-4 py-14 md:px-8 md:py-20 xl:px-20">
      <Glow color="blue" size={520} opacity={0.16} blur={70} className="left-1/2 top-10 hidden -translate-x-1/2 lg:block" />

      <div className="relative mx-auto flex max-w-[1280px] flex-col gap-10 md:gap-12">
        {showHeader ? (
          <SectionHeader
            eyebrow="Тарифы"
            title="Прозрачные тарифы"
            subtitle="Без скрытых платежей и подписок — выбираете формат под свою ситуацию."
          />
        ) : null}

        <div className="grid items-stretch gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {tariffs.map((tariff) => (
            <TariffCard key={tariff.id} tariff={tariff} {...(onSelect ? { onSelect } : {})} />
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2.5 text-[15px] md:text-[17px]">
          <span className="text-muted">Не уверены, какой формат подойдёт?</span>
          <Link to="/chat" className="font-semibold text-accent hover:opacity-80">
            Поможем выбрать
          </Link>
        </div>
      </div>
    </section>
  );
}
